import React, { useState, useEffect } from 'react';
import {
  VideoCameraIcon,
  ArrowDownTrayIcon,
  LinkIcon,
  ArrowPathIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import tutorApi from './tutorApi';

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatTime = (timeStr) => {
  if (!timeStr) return '';
  const [hours, minutes] = timeStr.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${ampm}`;
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let size = bytes;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i++;
  }
  return `${size.toFixed(1)} ${units[i]}`;
};

const Recordings = () => {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [copySuccess, setCopySuccess] = useState(null);

  const fetchRecordings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await tutorApi.getRecordings();
      setRecordings(data || []);
    } catch (err) {
      console.error('Error fetching recordings:', err);
      setError(err.message || 'Failed to load recordings');
      setRecordings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecordings();
  }, []);

  const handleDownload = async (bookingId, s3Key, fileName) => {
    const key = `${bookingId}-${s3Key}`;
    try {
      setDownloading(key);
      const url = await tutorApi.getRecordingDownloadUrl(bookingId, s3Key);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Download error:', err);
      setError(err.message || 'Failed to get download link');
    } finally {
      setDownloading(null);
    }
  };

  const handleCopyLink = async (bookingId, s3Key, fileName) => {
    const key = `${bookingId}-${s3Key}`;
    try {
      setDownloading(key);
      const url = await tutorApi.getRecordingDownloadUrl(bookingId, s3Key);
      await navigator.clipboard.writeText(url);
      setCopySuccess(key);
      setTimeout(() => setCopySuccess(null), 2500);
    } catch (err) {
      console.error('Copy error:', err);
      setError(err.message || 'Failed to get download link');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            Recordings
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Access your recorded tutoring sessions
          </p>
        </div>
        <div className="mt-4 flex md:ml-4 md:mt-0">
          <button
            type="button"
            onClick={fetchRecordings}
            disabled={loading}
            className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon
              className={`-ml-0.5 mr-1.5 h-5 w-5 text-gray-400 ${loading ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="bg-white shadow rounded-lg p-8">
          <div className="flex justify-center">
            <ArrowPathIcon className="h-10 w-10 animate-spin text-indigo-600" />
          </div>
          <p className="mt-4 text-center text-sm text-gray-500">
            Loading recordings...
          </p>
        </div>
      ) : recordings.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-indigo-100 mb-4">
              <VideoCameraIcon className="h-8 w-8 text-indigo-600" aria-hidden="true" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No Recordings Yet
            </h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Recordings from your completed tutoring sessions will appear here.
              Recordings are processed after each meeting—check back later if
              you recently finished a session.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
          {recordings.map((item) => (
            <div key={item.booking_id} className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {formatDate(item.date)} — {formatTime(item.start_time)} to{' '}
                    {formatTime(item.end_time)}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Student: {item.student_name}
                  </p>
                </div>
              </div>

              {item.recordings.length === 0 ? (
                <div className="mt-4 flex items-center gap-2 text-amber-700 bg-amber-50 rounded-lg px-4 py-3">
                  <DocumentTextIcon className="h-5 w-5 shrink-0" />
                  <p className="text-sm">
                    Recording not available yet. It may still be processing.
                    Try refreshing later.
                  </p>
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  {item.recordings.map((rec) => {
                    const recKey = `${item.booking_id}-${rec.s3_key}`;
                    const isBusy = downloading === recKey;
                    const copied = copySuccess === recKey;
                    return (
                      <div
                        key={rec.s3_key}
                        className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {rec.file_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(rec.size)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              handleDownload(item.booking_id, rec.s3_key, rec.file_name)
                            }
                            disabled={isBusy}
                            className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
                          >
                            <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                            Download
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handleCopyLink(item.booking_id, rec.s3_key, rec.file_name)
                            }
                            disabled={isBusy}
                            className="inline-flex items-center rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                          >
                            <LinkIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                            {copied ? 'Copied!' : 'Copy link'}
                          </button>
                        </div>
                        {copied && (
                          <p className="text-xs text-green-600 w-full sm:w-auto">
                            Paste the link in a new tab if the download doesn&apos;t start
                            automatically (useful for large files).
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Recordings;
